import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";

function euro(cents: number): string {
  return `${(cents / 100).toFixed(2).replace(".", ",")} €`;
}

type Status = "PAID" | "PROCESSING" | "SHIPPED" | "CANCELLED";

interface OrderCollar {
  id: string;
  publicId: string | null;
  active: boolean;
  type: "collar" | "tag";
  color: string | null;
  cat: { id: string; name: string };
}

interface Order {
  id: string;
  status: Status;
  priceCents: number;
  shippingMethod: string;
  createdAt: string;
  collars: OrderCollar[];
}

export function OrdersListPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["orders"], queryFn: () => api<Order[]>("/api/orders") });

  const toggle = useMutation({
    mutationFn: ({ catId, collarId, active }: { catId: string; collarId: string; active: boolean }) =>
      api(`/api/pets/${catId}/collars/${collarId}`, { method: "PATCH", body: JSON.stringify({ active }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });

  const label = (c: OrderCollar) =>
    c.type === "tag" ? "Mini-Anhänger" : `Halsband${c.color ? ` · ${t(`collarColors.${c.color}`)}` : ""}`;

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl font-semibold tracking-tight mb-2">{t("nav.orders")}</h1>
      <p className="text-sm text-slate-500 mb-6">
        Jedes Stück hat einen eigenen QR-Code. Verloren? Sperre genau dieses — die anderen laufen weiter.
      </p>

      {q.isLoading && <div className="text-slate-500">{t("common.loading")}</div>}
      {q.data?.length === 0 && <div className="text-slate-500">Noch keine Bestellungen.</div>}

      <div className="space-y-4">
        {q.data?.map((o) => (
          <div key={o.id} className="card p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-slate-500">
                {new Date(o.createdAt).toLocaleDateString()} · {euro(o.priceCents)} ·{" "}
                {o.shippingMethod === "paket" ? "Paket (versichert)" : "Brief (unversichert)"}
              </div>
              <span className="text-xs bg-slate-100 px-2 py-1 rounded">{t(`orders.status.${o.status}`)}</span>
            </div>

            <div className="mt-3 divide-y divide-slate-100">
              {o.collars.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <div className="min-w-0">
                    <span className="font-medium text-slate-800">{c.cat.name}</span>{" "}
                    <span className="text-slate-500">{label(c)}</span>
                    {c.publicId && <code className="ml-2 text-xs text-slate-500">{c.publicId}</code>}
                    {!c.active && <span className="ml-2 text-xs font-medium text-amber-700">gesperrt</span>}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {c.active ? (
                      <button
                        type="button"
                        onClick={() => toggle.mutate({ catId: c.cat.id, collarId: c.id, active: false })}
                        disabled={toggle.isPending}
                        className="text-xs font-medium text-red-600 underline underline-offset-2"
                      >
                        sperren
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => toggle.mutate({ catId: c.cat.id, collarId: c.id, active: true })}
                          disabled={toggle.isPending}
                          className="text-xs font-medium text-slate-600 underline underline-offset-2"
                        >
                          entsperren
                        </button>
                        <Link
                          to={`/app/pets/${c.cat.id}/order`}
                          className="text-xs font-medium text-brand-700 underline underline-offset-2"
                        >
                          Ersatz nachbestellen
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
